import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppShell from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { guidedColumn } from '../constants/guidedLayout';
const BareFrame = ({ children }) => <div data-testid="bare-frame">{children}</div>;

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// 🔑 bare (Jesse, 2026-10-19): the same page without the dashboard app shell, for the guided shell's in-shell view
// (pages/GuidedFlowPage). Everything else — the data, the actions — is this page unchanged.
export default function Settings({ bare = false } = {}) {
  const Frame = bare ? BareFrame : AppShell;
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  // ── Sharing the shopping range with the referring agent ───────────────────
  // 🚨 THE BORROWER IS THE ONLY ONE WHO CAN TURN THIS ON. Their approval ceiling
  // is their financial information; the agent needs it to find them homes, and
  // nobody else — not Doorly staff, not the agent — can consent on their behalf.
  const [rangeConsent, setRangeConsent] = useState(null);
  const [savingConsent, setSavingConsent] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [loanNumber, setLoanNumber] = useState(null);

  // ── Electronic delivery — E-Sign §101(c)(1)(B)(i) ────────────────────────
  // 🚨 THE RIGHT TO WITHDRAW IS NOT A SETTING, IT IS A STATUTORY RIGHT, and it
  // has to be exercisable at no cost and at any time. That is why it lives on
  // the Account card rather than behind a support request, and why the consent
  // page names this exact location.
  const [pwState, setPwState] = useState(null);   // null | 'sending' | 'sent' | 'failed'
  const [esign, setEsign] = useState(null);      // null = not loaded / not applicable
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawConfirm, setWithdrawConfirm] = useState(false);
  const [paperBy, setPaperBy] = useState(null);

  // Loan number isn't on the JWT (would bloat every authed request and
  // require token re-issue on assignment). Fetch from /auth/me — already
  // returns firstName/lastName/email so we get a single round-trip.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios
      .get(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!cancelled) setLoanNumber(r.data?.loanNumber || null); })
      .catch(() => { /* leave loanNumber null — Account card just hides that row */ });
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios.get(`${API_URL}/api/account-consent/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!cancelled) setEsign(r.data); })
      // ⚠️ A FAILED READ HIDES THE CARD RATHER THAN SHOWING A GUESS. Rendering
      // "electronic delivery is on" from a failed request would tell a borrower
      // something about their own consent that nobody checked.
      .catch(() => { if (!cancelled) setEsign(null); });
    return () => { cancelled = true; };
  }, [token]);

  const withdrawEsign = async () => {
    setWithdrawing(true);
    setError(null);
    try {
      const r = await axios.post(`${API_URL}/api/account-consent/withdraw`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      setPaperBy(r.data.paperBy || null);
      setEsign((prev) => ({ ...(prev || {}), electronicDelivery: false, withdrawnAt: r.data.withdrawnAt }));
      setWithdrawConfirm(false);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not withdraw consent. Please try again.');
    } finally {
      setWithdrawing(false);
    }
  };

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || '—';

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await axios.delete(`${API_URL}/api/user/account`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      logout();
      try {
        window.sessionStorage.setItem(
          'doorly_toast',
          JSON.stringify({ kind: 'success', text: 'Your account has been deleted.' })
        );
      } catch (_) { /* sessionStorage unavailable — drop the toast hint */ }
      navigate('/login', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not delete your account. Please try again or contact support@godoorly.com.');
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    let live = true;
    axios.get(`${API_URL}/api/borrower/range-consent`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (live) setRangeConsent(r.data); })
      // ⚠️ A FAILED LOAD HIDES THE CONTROL RATHER THAN RENDERING A DEFAULT. A
      // toggle drawn in the "off" position after a failed fetch invites a
      // borrower to switch on something whose current state is unknown.
      .catch(() => { if (live) setRangeConsent(null); });
    return () => { live = false; };
  }, [token]);

  const saveRangeConsent = async (granted) => {
    setSavingConsent(true);
    try {
      await axios.post(`${API_URL}/api/borrower/range-consent`, { granted },
        { headers: { Authorization: `Bearer ${token}` } });
      setRangeConsent((c) => ({ ...c, granted, decidedAt: new Date().toISOString() }));
    } catch (_) {
      // Re-read rather than assume — the server is the record.
      try {
        const r = await axios.get(`${API_URL}/api/borrower/range-consent`, { headers: { Authorization: `Bearer ${token}` } });
        setRangeConsent(r.data);
      } catch (__) { /* leave as-is */ }
    } finally { setSavingConsent(false); }
  };

  return (
    <Frame>
      {/* bare (the guided shell): the shell's column and gutter, so it lines up with the back link. */}
      <div style={bare ? { ...guidedColumn, padding: '12px 0 24px' } : { maxWidth: 720, margin: '0 auto', padding: '24px 20px' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 24px' }}>{bare ? 'Account' : 'Settings'}</h1>

        {/* Account section */}
        <section
          style={{
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            padding: 20,
            marginBottom: 16,
          }}
        >
          <h2 style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: '0 0 12px' }}>Account</h2>
          <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 8, columnGap: 12 }}>
            <dt style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>Name</dt>
            <dd style={{ margin: 0, fontSize: 13, color: '#111827', fontWeight: 600 }}>{fullName}</dd>
            <dt style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>Email</dt>
            <dd style={{ margin: 0, fontSize: 13, color: '#111827', fontWeight: 600, wordBreak: 'break-all' }}>{user?.email || '—'}</dd>
            {loanNumber && (
              <>
                <dt style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>Loan #</dt>
                <dd style={{ margin: 0, fontSize: 13, color: '#111827', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{loanNumber}</dd>
              </>
            )}
          </dl>

          {/* 🔑 RENDERED ONLY WHEN THE USER ACTUALLY HOLDS A CONSENT OR HAS
              WITHDRAWN ONE. Offering "withdraw" to somebody who never consented
              would be an offer to undo something that did not happen. */}
          {esign && (esign.electronicDelivery || esign.withdrawnAt) && (
            <div style={{ borderTop: '1px solid #f3f4f6', marginTop: 16, paddingTop: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
                Electronic delivery
              </div>
              <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 12px', lineHeight: 1.6 }}>
                {esign.electronicDelivery
                  ? 'You receive disclosures and loan documents electronically. You can withdraw '
                    + 'this consent at any time, free of charge. Withdrawing does not affect your '
                    + 'application or your loan, and records already delivered remain valid — we '
                    + 'will switch to mail within 3 business days.'
                  : 'Electronic delivery is off. We are sending your records by mail.'}
              </p>
              {paperBy && (
                <p style={{ fontSize: 12, color: '#111827', fontWeight: 600, margin: '0 0 12px' }}>
                  Paper delivery begins by {new Date(paperBy).toLocaleDateString()}.
                </p>
              )}
              {esign.electronicDelivery && !withdrawConfirm && (
                <button
                  type="button"
                  onClick={() => setWithdrawConfirm(true)}
                  style={{
                    borderRadius: 8, padding: '9px 16px', cursor: 'pointer', fontSize: 13,
                    fontWeight: 700, border: '2px solid #e5e7eb', background: '#ffffff', color: '#6b7280',
                  }}
                >
                  Withdraw consent
                </button>
              )}
              {esign.electronicDelivery && withdrawConfirm && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    disabled={withdrawing}
                    onClick={withdrawEsign}
                    style={{
                      borderRadius: 8, padding: '9px 16px', cursor: withdrawing ? 'wait' : 'pointer',
                      fontSize: 13, fontWeight: 700, border: '2px solid #3a6ad9',
                      background: '#3a6ad9', color: '#ffffff',
                    }}
                  >
                    {withdrawing ? 'Withdrawing…' : 'Yes, withdraw'}
                  </button>
                  <button
                    type="button"
                    disabled={withdrawing}
                    onClick={() => setWithdrawConfirm(false)}
                    style={{
                      borderRadius: 8, padding: '9px 16px', cursor: 'pointer', fontSize: 13,
                      fontWeight: 700, border: '2px solid #e5e7eb', background: '#ffffff', color: '#6b7280',
                    }}
                  >
                    Keep electronic
                  </button>
                </div>
              )}
            </div>
          )}
        </section>

        {/* 🔑 PASSWORD (Jesse, 2026-10-19): borrowers have no change-password endpoint; the reset link is the one
            path, so this sends it to the address on the account (POST /api/auth/forgot-password). */}
        <section style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>Password</h2>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
            {pwState === 'sent'
              ? `We sent a link to ${user?.email || 'your email'}. Open it to set a new password.`
              : pwState === 'failed'
                ? 'We could not send the link just now. Please try again.'
                : 'We will email you a link to set a new password.'}
          </p>
          <button type="button" data-testid="account-change-password" disabled={pwState === 'sending' || !user?.email}
            onClick={async () => {
              setPwState('sending');
              try { await axios.post(`${API_URL}/api/auth/forgot-password`, { email: user.email }); setPwState('sent'); }
              catch (_) { setPwState('failed'); }
            }}
            style={{ borderRadius: 8, padding: '9px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 700,
              border: '2px solid #e5e7eb', background: '#ffffff', color: '#111827' }}>
            {pwState === 'sending' ? 'Sending…' : 'Email me a link to change my password'}
          </button>
        </section>

        {/* Sharing with your agent — only rendered when the borrower actually has
            one and the preference loaded. */}
        {rangeConsent?.applicable && (
          <section style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 16 }}>
            <h2 style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>
              Sharing with {rangeConsent.partnerName}
            </h2>
            <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 14px', lineHeight: 1.6 }}>
              {rangeConsent.consentText}
            </p>
            {/* 🚨 A YES/NO PAIR, MATCHING THE APPLICATION. A single toggle cannot
                express "I was asked and said no" separately from "I have not
                answered", and the partner is shown a different message for each
                — so a borrower who never answered must never be able to land in
                the declined state by accident. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {[{ v: true, l: 'Yes, share it' }, { v: false, l: 'No, keep it private' }].map((opt) => {
                const active = rangeConsent.decidedAt != null && rangeConsent.granted === opt.v;
                return (
                  <button
                    key={String(opt.v)}
                    type="button"
                    aria-pressed={active}
                    disabled={savingConsent}
                    onClick={() => saveRangeConsent(opt.v)}
                    style={{
                      borderRadius: 8, padding: '9px 16px', cursor: savingConsent ? 'wait' : 'pointer',
                      fontSize: 13, fontWeight: 700,
                      border: `2px solid ${active ? '#3a6ad9' : '#e5e7eb'}`,
                      background: active ? '#eef3fd' : '#ffffff',
                      color: active ? '#3a6ad9' : '#6b7280',
                    }}
                  >
                    {opt.l}
                  </button>
                );
              })}
              <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginLeft: 4 }}>
                {savingConsent ? 'Saving…'
                  : rangeConsent.decidedAt == null ? 'Not answered yet'
                    : rangeConsent.granted ? 'Shared' : 'Not shared'}
              </span>
            </div>
            {/* ⚠️ SAID OUT LOUD BECAUSE A BORROWER SHOULD NOT HAVE TO INFER IT. */}
            <p style={{ fontSize: 11.5, color: '#9ca3af', margin: '12px 0 0', lineHeight: 1.5 }}>
              You can change this at any time. Nothing else from your file is shared either way.
            </p>
          </section>
        )}

        {/* Danger zone — visually separated, last on the page */}
        <section
          style={{
            background: '#ffffff',
            border: '1px solid #fde2e4',
            borderRadius: 12,
            padding: 20,
          }}
        >
          <h2 style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>
            Delete Account
          </h2>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 16px', lineHeight: 1.5 }}>
            Permanently delete your Doorly account. Your personal information will be
            removed; loan and disclosure records are retained as required by law.
          </p>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            style={{
              background: 'transparent',
              color: '#ff082f',
              border: '1px solid #ff082f',
              borderRadius: 10,
              padding: '10px 18px',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Delete Account
          </button>
        </section>
      </div>

      {/* Confirmation modal */}
      {confirmOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="safe-modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(17, 24, 39, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: 20,
          }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: 14,
              maxWidth: 420,
              width: '100%',
              padding: 24,
              boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
            }}
          >
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>
              Delete your account?
            </h3>
            <p style={{ fontSize: 13, color: '#4b5563', margin: '0 0 20px', lineHeight: 1.6 }}>
              This will permanently delete your Doorly account and remove your personal
              information. Your application data will be retained as required by law.
              This action cannot be undone.
            </p>
            {error && (
              <div
                style={{
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  color: '#991b1b',
                  fontSize: 12,
                  borderRadius: 8,
                  padding: 10,
                  marginBottom: 14,
                }}
              >
                {error}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                type="button"
                disabled={deleting}
                onClick={() => { setConfirmOpen(false); setError(null); }}
                style={{
                  background: '#f3f4f6',
                  color: '#111827',
                  border: 'none',
                  borderRadius: 10,
                  padding: '10px 18px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={handleDelete}
                style={{
                  background: '#ff082f',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: 10,
                  padding: '10px 18px',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  opacity: deleting ? 0.7 : 1,
                }}
              >
                {deleting ? 'Deleting…' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Frame>
  );
}
